import { showToast } from '@bp/ui-components';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { eventJobRunningStore } from '../stores/eventJobRunningStore';
import {
  EventJobStatus,
  EventJobsDocument,
  type EventJobsQuery,
  type EventJobsQueryVariables,
} from '../types/planung-graphql-client-defs';
import { apiHelper } from './apiHelper';
import { urqlClient } from './urqlClient';

/**
 * Prüft live (`network-only`), ob für die Organisation aktuell ein EventJob läuft.
 *
 * Wird beim Klick aufgerufen – so wird auch ein von einem ANDEREN User gestarteter
 * Job erkannt. Im Ruhezustand pollt der `EventJobRunningProvider` nämlich nicht, das
 * Context-Flag allein wäre also evtl. veraltet.
 */
export const isEventJobRunningLive = async (): Promise<boolean> => {
  const { data } = await urqlClient
    .query<EventJobsQuery, EventJobsQueryVariables>(
      EventJobsDocument,
      { runningOnly: true },
      { requestPolicy: 'network-only' },
    )
    .toPromise();

  return data?.eventJobs.some((job) => job.status === EventJobStatus.Running) ?? false;
};

/**
 * Guard, der am ANFANG jeder Aktion aufgerufen wird, die (direkt oder indirekt) einen
 * EventJob auslösen würde. Prüft live, ob bereits ein Job läuft.
 *
 * Wir prüfen hier statt die auslösenden Buttons anhand des (im Ruhezustand nicht
 * gepollten und daher evtl. veralteten) Context-Flags zu deaktivieren.
 *
 * Falls ein Job läuft: macht den laufenden Status sichtbar (Tick/Banner via
 * `markStarted`) + Hinweis-Toast und gibt `true` zurück → der Aufrufer MUSS die
 * komplette Aktion abbrechen (also auch vorgelagerte Mutationen unterlassen).
 * Falls keiner läuft: `false` → die Aktion darf weiterlaufen.
 */
export const blockIfEventJobRunning = async (): Promise<boolean> => {
  if (await isEventJobRunningLive()) {
    // Laufenden (ggf. fremden) Job reflektieren und den 5s-Tick anstoßen.
    eventJobRunningStore.markStarted();
    showToast(t('synchronizations.alreadyRunning'), { type: 'warning', autoClose: 4000 });
    return true;
  }
  return false;
};

/**
 * Nach dem Anlegen, Ändern oder Löschen von Ferien stößt das Backend selbst den Abgleich der Termine an
 * (`change-holiday.workflow.ts`), aber nur für Tage ab heute. Deshalb nur dann auf "läuft" schalten, wenn die alten
 * oder neuen Ferien bis heute oder später reichen; sonst stünde der Hinweis bis zur TTL grundlos da.
 */
export const markEventJobStartedForHolidays = (...holidays: ({ end: string | Date } | null | undefined)[]) => {
  if (holidays.some((holiday) => holiday && !dayjs(holiday.end).isBefore(dayjs(), 'day'))) {
    eventJobRunningStore.markStarted();
  }
};

export const deleteEvents = async ({
  organizationUuid,
  schoolYearUuid,
}: {
  organizationUuid: string;
  schoolYearUuid: string;
}) => {
  await apiHelper<void>({
    method: 'DELETE',
    path: `/events/${organizationUuid}/${schoolYearUuid}`,
  });
};

export const createEvents = async ({
  organizationUuid,
  schoolYearUuid,
}: {
  organizationUuid: string;
  schoolYearUuid: string;
}) => {
  eventJobRunningStore.markStarted();
  await apiHelper<void>({
    method: 'POST',
    path: `/events/${organizationUuid}/${schoolYearUuid}`,
  });
};

/**
 * Stösst die Event-Erzeugung für einen Datumsbereich an. Das Backend verwirft den Auftrag,
 * wenn der Bereich nicht vollständig im übergebenen Schuljahr liegt – dann wird weder ein Job
 * eingereiht noch das "läuft"-Flag gesetzt.
 *
 * @returns ob der Job tatsächlich eingereiht wurde
 */
export const createEventsForDateRange = async ({
  organizationUuid,
  schoolYearUuid,
  from,
  to,
  epochSubjectContainerUuid,
}: {
  organizationUuid: string;
  schoolYearUuid: string;
  from: string;
  to: string;
  epochSubjectContainerUuid?: string;
}): Promise<boolean> => {
  const result = await apiHelper<{ success?: boolean } | undefined>({
    method: 'POST',
    path: `/events/${organizationUuid}/date-range`,
    type: 'json',
    suppressErrorToast: true,
    data: JSON.stringify({ schoolYearUuid, from, to, epochSubjectContainerUuid }),
  });

  if (!result?.success) {
    showToast(t('synchronizations.startFailed'), { type: 'error', autoClose: 5000 });
    return false;
  }

  // Erst jetzt optimistisch auf "läuft" schalten: vorher wäre das Banner auch dann bis zur
  // TTL sichtbar, wenn das Backend den Auftrag abgelehnt hat.
  eventJobRunningStore.markStarted();
  return true;
};

export const createEventsForVersion = async ({
  organizationUuid,
  timetableVersionUuid,
}: {
  organizationUuid: string;
  timetableVersionUuid: string;
}) => {
  eventJobRunningStore.markStarted();
  await apiHelper<void>({
    method: 'POST',
    path: `/events/${organizationUuid}/timetable-version/${timetableVersionUuid}`,
  });
};
